import { OktaAuth, toRelativeUrl } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { useMemo, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { createOktaAuth } from "./okta-config.js";

/**
 * Wraps the shell in Okta's React SDK provider. The security-critical SDK configuration lives in
 * `okta-config.ts` (memory token storage, PKCE). This component only wires the SDK to React Router
 * for the post-sign-in redirect restore.
 *
 * Because the shell mounts at `/` with NO router basename, `restoreOriginalUri` just strips the
 * origin to an in-app relative path. A captured `originalUri` of `/admin/mapping/commission`
 * already IS the correct in-app path — no basename rewriting is required (unlike the former
 * separate admin SPA, which ran under `basename="/admin"`).
 */
interface OktaProviderProps {
  readonly children: ReactNode;
  /** Test seam: inject a preconfigured OktaAuth instead of constructing one from env. */
  readonly oktaAuth?: OktaAuth;
}

export function OktaProvider({ children, oktaAuth }: OktaProviderProps): JSX.Element {
  const navigate = useNavigate();

  const instance = useMemo(() => oktaAuth ?? createOktaAuth(), [oktaAuth]);

  // Strip the origin so React Router navigates with a path, never an absolute (open-redirect) URL.
  const restoreOriginalUri = (_oktaAuth: OktaAuth, originalUri?: string): void => {
    navigate(toRelativeUrl(originalUri ?? "/", window.location.origin), { replace: true });
  };

  return (
    <Security oktaAuth={instance} restoreOriginalUri={restoreOriginalUri}>
      {children}
    </Security>
  );
}
